import React, { FC, useContext, useEffect, useState } from 'react';
import { tenderOffersApiRequests } from 'utils/api-requests/tender';
import TenderContext from 'utils/context/TenderContext';
import { createUniqueFilterName } from 'utils/createUniqueFilterName';
import { generateOptionsFromEnum } from 'utils/generateOptionsFromEnum';
import { areaOptions, priceOptions, pricePerSquareMeterSaleOptions, scheduleOptions } from 'utils/options';
import { getTenderValueFromState } from 'utils/state-managment/tender/tenderFilter';
import { InputType } from 'utils/types/InputTypes';
import { TenderFiltersStateType } from 'utils/types/Tender';
import { TenderFilterResponseTypesEnum } from 'utils/types/TenderModels';

import Button from 'components/atom/Button';
import StickyBottom from 'components/common/FiltersRenderer/StickyBottom';
import V2FiltersRenderer from 'components/common/FiltersRenderer/V2FiltersRenderer';
import { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import { activeNotification } from 'components/functions/activeNotification';
import { v2getVoivodeshipCities } from 'components/functions/locations';

type FiltersTenderProperties = object;

const FiltersTender: FC<FiltersTenderProperties> = () => {
    const {
        tenderFiltersState,
        tenderOfferState,
        handleChange,
        handleDeleteFilter,
        handleSetFiltersValues,
        handleChangeDateList,
        getSavedFilters,
        clearFilters,
        loadTenderOffers
    } = useContext(TenderContext);

    const [isFilterButtonVisible, setIsFilterButtonVisible] = useState(false);

    const getCities = async (voivodeshipId: number) => {
        const newLocations = await v2getVoivodeshipCities(voivodeshipId, tenderFiltersState.locations);
        handleChange('locations', newLocations || []);
    };

    const handleRemoveSavedLocation = (locationId: number) => {
        const savedLocations = tenderFiltersState.savedLocations;
        delete savedLocations[locationId];

        handleChange('savedLocations', savedLocations);
    };

    const locationCompleted = Object.keys(tenderFiltersState.savedLocations).length > 0;

    const handleSubmit = () => {
        if (!locationCompleted) {
            activeNotification('Popraw wpisane dane', 'Musisz wybrać poprawną miejscowość', 'warning');
        } else {
            loadTenderOffers();
            document.body.scrollTo(0, 0);
        }
    };

    const handleSaveOrUpdateFilter = async () => {
        if (!locationCompleted) {
            activeNotification('Popraw wpisane dane', 'Musisz wybrać poprawną miejscowość', 'warning');

            return;
        }

        const existFilter = tenderFiltersState.savedFilter?.find(
            (filter) => filter.filterId === tenderFiltersState.filterId
        );

        const existName = tenderFiltersState.savedFilter?.find(
            (filter) => filter.name === tenderFiltersState.name
        );

        let id;

        if (existName && !tenderFiltersState.filterId) {
            activeNotification('Nazwa filtra', 'Istnieje już filtr o takiej nazwie', 'warning');

            return;
        }

        if (existFilter) {
            await tenderOffersApiRequests.updateFilter({
                ...getTenderValueFromState(tenderFiltersState)
            });
        } else {
            const resp = await tenderOffersApiRequests.addFilter({
                ...getTenderValueFromState(tenderFiltersState)
            });

            id = resp?.data;
        }

        getSavedFilters(id);
    };

    const handleCreateFromExistFilter = async () => {
        if (!locationCompleted) {
            activeNotification('Popraw wpisane dane', 'Musisz wybrać poprawną miejscowość', 'warning');

            return;
        }

        const newName = createUniqueFilterName(tenderFiltersState.name!, tenderFiltersState.savedFilter);

        const newFilterData = {
            ...getTenderValueFromState(tenderFiltersState),
            name: newName
        };

        const resp = await tenderOffersApiRequests.addFilter(newFilterData);

        const id = resp?.data;

        getSavedFilters(id);
    };

    useEffect(() => {
        getSavedFilters();
    }, []);

    const handleChangeNotificationAlert = async (valueName: string, id: number) => {
        const fieldName = valueName === 'pushAlert' ? 'alertPush' : valueName;
        const updatedState = {
            ...tenderFiltersState,
            [fieldName]: !tenderFiltersState[fieldName as keyof TenderFiltersStateType]
        };

        await tenderOffersApiRequests.updateFilter(getTenderValueFromState(updatedState));
        getSavedFilters(id);
    };

    const tenderFiltersStructure = [
        {
            accordionTitle: 'Podstawowe',
            hideTitle: true,
            filters: [
                {
                    title: 'Zapisane filtry',
                    type: InputType.FILTER_AUTOCOMPLETE,
                    icon: IconEnum.BOOKMARK,
                    placeholder: 'Wybierz filtr...',
                    options: {
                        [InputType.FILTER_AUTOCOMPLETE]: {
                            contextFunctions: {
                                handleDeleteFilter,
                                handleSetFiltersValues
                            },
                            modulesProps: {
                                filterId: tenderFiltersState.filterId ?? null,
                                options: tenderFiltersState.savedFilter.map((x) => ({
                                    ...x,
                                    pushAlert: x.alertPush,
                                    id: x.filterId
                                }
                                ))
                            }
                        }
                    }
                },
                {
                    title: 'Lokalizacja',
                    type: InputType.LOCATION,
                    helperText: 'Wybierz z listy interesującą Ciebie lokalizację. Możesz zaznaczyć dowolną ilość.',
                    icon: IconEnum.MAP_PIN,
                    placeholder: 'Szukaj lokalizacji...',
                    options: {
                        [InputType.LOCATION]: {
                            modulesProps: {
                                locations: tenderFiltersState.locations,
                                searchLocationResult: tenderFiltersState.searchLocationResult,
                                savedLocations: tenderFiltersState.savedLocations,
                                savedLocationsFieldName: 'savedLocations',
                                cityFieldName: 'city'
                            },
                            contextFunctions: {
                                getCities,
                                handleRemoveSavedLocation,
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Data',
                    type: InputType.FLEXIBLE_DATE,
                    helperText: 'Wybierz, z jakiego okresu chcesz wyświetlić oferty. Wybierz opcję "Przedział dat" aby precyzyjnie wskazać datę. Ten parametr nie wpływa na natychmiastowe wysyłanie alertów.',
                    icon: IconEnum.CALENDAR,
                    placeholder: 'Data dodania oferty...',
                    containerActionText: 'Przedział dat',
                    containerActionClick: () => handleChange('dateRangeFilter', !tenderFiltersState.dateRangeFilter),
                    options: {
                        [InputType.FLEXIBLE_DATE]: {
                            modulesProps: {
                                date: tenderFiltersState.offerAdded,
                                dateRangeFilter: tenderFiltersState.dateRangeFilter,
                                daysAmount: tenderFiltersState.daysAmount,
                                fieldName: 'offerAdded'
                            },
                            contextFunctions: {
                                handleChange,
                                handleChangeDateList
                            }
                        }
                    }
                }
            ]
        },
        {
            accordionTitle: 'Parametry',
            hideTitle: true,
            filters: [
                {
                    title: 'Typ nieruchomości',
                    type: InputType.MULTIPLE_SELECT,
                    icon: IconEnum.LEAVE,
                    placeholder: 'Wybierz rodzaj...',
                    options: {
                        [InputType.MULTIPLE_SELECT]: {
                            modulesProps: {
                                value: tenderFiltersState.types,
                                options: generateOptionsFromEnum(TenderFilterResponseTypesEnum),
                                optionsIcon: IconEnum.TWO_TYPE,
                                fieldName: 'types'
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Cena',
                    type: InputType.RANGE_PRICE_SELECT,
                    icon: IconEnum.PRICE,
                    placeholder: '',
                    options: {
                        [InputType.RANGE_PRICE_SELECT]: {
                            modulesProps: {
                                value: tenderFiltersState.price,
                                fieldName: 'price',
                                options: priceOptions
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Cena za metr',
                    type: InputType.RANGE_PRICE_SELECT,
                    icon: IconEnum.PRICE_PART,
                    placeholder: '',
                    options: {
                        [InputType.RANGE_PRICE_SELECT]: {
                            modulesProps: {
                                value: tenderFiltersState.pricePerSquareMeter,
                                fieldName: 'pricePerSquareMeter',
                                options: pricePerSquareMeterSaleOptions
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Metraż',
                    type: InputType.RANGE_METERS_SELECT,
                    icon: IconEnum.SQUARE,
                    placeholder: '',
                    options: {
                        [InputType.RANGE_METERS_SELECT]: {
                            modulesProps: {
                                value: tenderFiltersState.area,
                                fieldName: 'area',
                                options: areaOptions
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Słowa pożądane',
                    type: InputType.KEYWORDS_TEXT,
                    helperText: 'Podaj słowa kluczowe, które mają zawierać się w tytule lub opisie ogłoszenia, rozdzielając je przecinkiem. Więcej w zakładce Pomoc',
                    icon: IconEnum.DICTIONARY,
                    placeholder: 'Wpisz słowo...',
                    options: {
                        [InputType.KEYWORDS_TEXT]: {
                            modulesProps: {
                                value: tenderFiltersState.wantedKeywords.filter(Boolean) as string[],
                                fieldName: 'wantedKeywords',
                                icon: IconEnum.SEARCH,
                                actionIcon: IconEnum.PLUS,
                                errorMessage: 'Słowo musi mieć minimum 3 znaki',
                                minLength: 3
                            },
                            contextFunctions: {
                                inputValidation: (val: string | number | null | undefined) => (val as string).length > 0 && (val as string).length < 3,
                                handleChange
                            }
                        }
                    }
                },
                {
                    title: 'Słowa niepożądane',
                    type: InputType.KEYWORDS_TEXT,
                    helperText: 'Podaj słowa kluczowe, które nie mają zawierać się w tytule lub opisie ogłoszenia, rozdzielając je przecinkiem. Więcej w zakładce Pomoc',
                    icon: IconEnum.DICTIONARY,
                    placeholder: 'Wpisz słowo...',
                    options: {
                        [InputType.KEYWORDS_TEXT]: {
                            modulesProps: {
                                value: tenderFiltersState.unwantedKeywords.filter(Boolean) as string[],
                                fieldName: 'unwantedKeywords',
                                icon: IconEnum.SEARCH,
                                actionIcon: IconEnum.PLUS,
                                errorMessage: 'Słowo musi mieć minimum 3 znaki',
                                minLength: 3
                            },
                            contextFunctions: {
                                inputValidation: (val: string | number | null | undefined) => (val as string).length > 0 && (val as string).length < 3,
                                handleChange
                            }
                        }
                    }
                }
            ]
        },
        {
            accordionTitle: 'Ustawienia',
            hideTitle: true,
            filters: [
                {
                    title: 'Zerowe lub puste wartości',
                    type: InputType.RADIO,
                    helperText: 'Wybierz czy mają pojawiać się ogłoszenia, które nie spełniają parametrów filtra z powodu braku danych. Więcej w zakładce Pomoc',
                    icon: IconEnum.OPTIONS,
                    placeholder: '',
                    options: {
                        [InputType.RADIO]: {
                            modulesProps: {
                                value: tenderFiltersState.includeZeroArea,
                                fieldName: 'includeZeroArea'
                            },
                            contextFunctions: {
                                handleChange
                            }
                        }
                    }
                }
            ]
        },
        {
            accordionTitle: 'Dodatkowe',
            hideTitle: true,
            filters: [
                {
                    title: 'Harmonogram',
                    type: InputType.AUTOCOMPLETE,
                    helperText: 'Co jaki okres chcesz otrzymywać powiadomienia? Jeżeli wybierzesz "Natychmiast" to każda oferta zostanie wysłana w osobnym mailu. w pozostałych przypadkach otrzymasz jednego maila np. co 5 min z listą ogłoszeń, które pojawiły się w tym czasie.',
                    icon: IconEnum.TIMER,
                    placeholder: 'Wybierz godziny...',
                    options: {
                        [InputType.AUTOCOMPLETE]: {
                            contextFunctions: {
                                handleChange
                            },
                            modulesProps: {
                                value: tenderFiltersState.notificationsDelay,
                                fieldName: 'notificationsDelay',
                                options: scheduleOptions
                            }
                        }
                    }
                }
            ]
        },
        {
            accordionTitle: 'Podsumowanie',
            hideTitle: true,
            isWideSpace: true,
            lastElement: true,
            filters: [
                {
                    title: 'Zapisz nowy filtr',
                    type: InputType.TEXT,
                    icon: IconEnum.DICTIONARY,
                    placeholder: 'Wpisz nazwę...',
                    containerActionText: tenderFiltersState.filterId ? 'Duplikuj' : undefined,
                    containerActionClick: handleCreateFromExistFilter,
                    options: {
                        [InputType.TEXT]: {
                            modulesProps: {
                                value: tenderFiltersState.name || '',
                                fieldName: 'name',
                                inputIcon: IconEnum.BOOKMARK_WHITE,
                                actionIcon: IconEnum.ARROW_RIGHT,
                                important: true,
                                isSaveFilterButton: true,
                                isDuplicateFilterButton: tenderFiltersState.filterId ? true : undefined
                            },
                            contextFunctions: {
                                handleChange,
                                inputActionHandler: handleSaveOrUpdateFilter
                            }
                        }
                    }
                },
                {
                    title: 'Aktywacja powiadomień',
                    type: InputType.NOTIFICATIONS,
                    helperText: 'Przed aktywacją powiadomienia zapisz filtr',
                    icon: IconEnum.ALERT,
                    placeholder: '',
                    options: {
                        [InputType.NOTIFICATIONS]: {
                            contextFunctions: {
                                handleChange: handleChangeNotificationAlert
                            },
                            modulesProps: {
                                alertEmail: tenderFiltersState.alertEmail,
                                alertSms: tenderFiltersState.alertSms,
                                pushAlert: tenderFiltersState.alertPush,
                                filterId: tenderFiltersState.filterId ?? null
                            }
                        }
                    }
                },
                {
                    title: 'Zastosuj filtry',
                    type: InputType.SAVE_BUTTON,
                    icon: IconEnum.DICTIONARY,
                    placeholder: 'Przeglądaj oferty...',
                    options: {
                        [InputType.SAVE_BUTTON]: {
                            contextFunctions: {
                                onChangeVisibleButtonFilter: setIsFilterButtonVisible,
                                onActionClick: clearFilters,
                                onClick: handleSubmit
                            },
                            modulesProps: {
                                actionText: 'Resetuj'
                            }
                        }
                    }
                }
            ]
        }
    ];

    return (
        <V2FiltersRenderer elementsToRender={tenderFiltersStructure} type={'tender'} bottomActions={
            <StickyBottom
                isVisible={isFilterButtonVisible}
                active={tenderOfferState.tenderOfferList?.content?.length > 0 || tenderOfferState.tenderOfferList.empty}
                className={'sticky-filter-button'}>
                <InputContainer title={'Zastosuj filtry'} icon={IconEnum.DICTIONARY}
                    actionText={'Resetuj'}
                    onActionClick={clearFilters}>
                    <Button onClick={handleSubmit}>
                        {'Przeglądaj oferty...'}
                    </Button>
                </InputContainer>
            </StickyBottom>
        }/>
    );
};

export default FiltersTender;
